import { useContext } from 'react';
import { AuthContext } from '../../providers/AuthProvider';
import { ApiMethod } from '../models/api-types.type';
import { routes } from '../../assets/constants';

export interface DeviceSession {
  id: string;
  label?: string;
  browser?: string;
  os?: string;
  deviceType?: 'desktop' | 'mobile' | 'tablet' | 'unknown';
  ip?: string;
  location?: { city?: string; country?: string; countryCode?: string };
  email: string;
  createdAt?: string;
  lastSeenAt: string;
  /** Used within the idle window; older devices show as idle. */
  active: boolean;
  /** The device making this request. */
  current: boolean;
}

export interface SeatOverview {
  /** How long a device stays "active" without being used. */
  idleMinutes: number;
  currentSessionId?: string;
  sessions: DeviceSession[];
  /** Super-admin view only: the vendor's accounts against its plan (-1 = unlimited). */
  seats?: { limit: number; used: number; reserved: number };
}

/** A vendor's signed-in devices; the admin calls act on any vendor. */
export const useDevicesApi = () => {
  const { sendAuthGuardedRequest } = useContext(AuthContext);

  const getOverview = () => sendAuthGuardedRequest(ApiMethod.GET, routes.devices.overview) as Promise<SeatOverview>;
  const rename = (id: string, label: string) => sendAuthGuardedRequest(ApiMethod.PATCH, routes.devices.device(id), { label });
  const revoke = (id: string) => sendAuthGuardedRequest(ApiMethod.DELETE, routes.devices.device(id));
  const revokeOthers = () => sendAuthGuardedRequest(ApiMethod.POST, routes.devices.revokeOthers) as Promise<{ count: number }>;

  const getVendorOverview = (vendorId: string) =>
    sendAuthGuardedRequest(ApiMethod.GET, routes.devices.adminOverview(vendorId)) as Promise<SeatOverview>;
  const revokeForVendor = (vendorId: string, id: string) =>
    sendAuthGuardedRequest(ApiMethod.DELETE, routes.devices.adminDevice(vendorId, id));

  return { getOverview, rename, revoke, revokeOthers, getVendorOverview, revokeForVendor };
};
